<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { IconBarButton } from '@oat-sa-private/ui-elements';

    /**
     * Numeric input component consisting of decrement/increment buttons for a preset value range.
     */

    /**
     * @typedef {Object} Props
     * @property {Number} value
     * @property {Number} min
     * @property {Number} max
     * @property {Number} step
     * @property {String} ariaLabelDecr
     * @property {String} ariaLabelIncr
     * @property {Boolean} [disabled]
     * @property {Function} onChange
     */
    /** @type {Props} */
    // prettier-ignore
    let {
        value = $bindable(),
        min,
        max,
        step,
        ariaLabelDecr,
        ariaLabelIncr,
        disabled = false,
        onChange
    } = $props();

    let position;

    function handleStepperChange() {
        onChange?.({
            value,
            position,
            nonDefault: value !== min
        });
    }

    /**
     * Handle when '-' button is clicked
     * @param {Event} event
     */
    function handleStepperDecr(event) {
        if (value > min) {
            const newValue = value - step;
            value = Math.max(min, newValue);
            position = { x: event.clientX, y: event.clientY };
            handleStepperChange();
        }
    }

    /**
     * Handle when '+' button is clicked
     * @param {Event} event
     */
    function handleStepperIncr(event) {
        if (value < max) {
            const newValue = value + step;
            value = Math.min(max, newValue);
            position = { x: event.clientX, y: event.clientY };
            handleStepperChange();
        }
    }
</script>

<style>
    .stepper {
        white-space: nowrap;
        margin-inline-end: -1.5rem;
        display: flex;
        /* The 2 buttons should appear to render [-][+], but in tab order the [+] should receive focus first */
        flex-direction: row-reverse;
    }
    :global(.inverted .stepper > .actionable.secondary) {
        border-color: transparent;

        &:hover {
            background-color: var(--color-bg-info);
        }
    }
</style>

<span class="stepper" class:changed-control={value !== min}>
    <IconBarButton
        skin="secondary"
        size="base-12"
        icon="plus-16"
        shape="circular"
        visuallyDisabled={value >= max}
        label={ariaLabelIncr}
        ariaLabel={ariaLabelIncr}
        {disabled}
        on:click={handleStepperIncr} />
    <IconBarButton
        skin="secondary"
        size="base-12"
        icon="minus-16"
        shape="circular"
        visuallyDisabled={value <= min}
        label={ariaLabelDecr}
        ariaLabel={ariaLabelDecr}
        {disabled}
        on:click={handleStepperDecr} />
</span>
